import styled from '@emotion/styled'
import { breakpoint, calcRem, colors, fonts } from '../../../../__styling'
import { RibbonOptions } from '../../helpers/BreachWallTypes'
import {
    PackageOptions,
    PackageThemeProps,
} from '../BreachWallContent/BreachWallContent'
import { StyledSubscriptionRadioInput as SubscriptionRadioInput } from '../SubscriptionPriceDisplay/SubscriptionPriceDisplay.styled'

export const StyledSubscriptionPackageContainer = styled(
    'div',
)<PackageThemeProps>(({ packageOptions, ribbonType, theme, breachOptions }) => [
    {
        display: 'flex',
        flexDirection: 'column',
        padding: calcRem(24, 24, 16, 24),
        alignItems: 'center',
        gap: calcRem(16),
        flex: '1 0 0',
        alignSelf: 'stretch',
        position: 'relative',

        borderRadius: calcRem(8),
        background: '#FFF',
        boxShadow: `6px 6px 26px 0 rgba(0,0,0, 0.21)`,

        maxWidth: calcRem(344),

        [breakpoint('xs')]: {
            width: calcRem(344),
        },
    },
    // If it's recommended, we apply the following styles to make it stand out
    packageOptions.isRecommended === true && {
        border:
            ribbonType === 'bluePillGradient'
                ? `6px solid #EDF2FF`
                : `2px solid ${theme.colors.brand}`,

        // Create a ribbon effect on the before
        '&::before': {
            content:
                breachOptions.breachType === 'digital-intro-default'
                    ? `'Most Popular'`
                    : `'Best Value!'`,
            position: 'absolute',
            top: calcRem(-20),

            height: calcRem(40),
            padding: calcRem(0, 16),
            alignContent: 'center',

            background:
                ribbonType === 'bluePillGradient'
                    ? 'linear-gradient(95deg, #678EFF 2%, #3065FF 30%, #032CA2 93%), #FFF'
                    : theme.colors.brand,
            fontFamily: fonts.thewest.sansSerif,
            fontSize: calcRem(16),
            fontWeight: 600,
            lineHeight: calcRem(18),
            color: '#FFF',

            borderRadius: calcRem(99),
            boxShadow: `0 4px 20px 0 rgba(0,0,0, 0.11)`,
        },
    },
    // If the columns are to be positioned in a grid layout, we apply the following styles to enable that
    packageOptions.gridColumnPosition && {
        gridRow: packageOptions.gridColumnPosition.mobile,

        [breakpoint('lg')]: {
            gridColumn: packageOptions.gridColumnPosition.desktop,
            gridRow: 1,
        },
    },
    // This campaign only has 2 offers, so looks better at Tablet with 2 columns
    breachOptions.breachType === '50-off-campaign' &&
        packageOptions.gridColumnPosition && {
            // Tablet
            [breakpoint('sm')]: {
                gridColumn: packageOptions.gridColumnPosition.desktop,
                gridRow: '1',
            },
        },
])

export const StyledSubscriptionContentContainer = styled('div')({
    display: 'flex',
    paddingTop: calcRem(8),
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: calcRem(16),
    flex: `1 0 0`,
    alignSelf: 'stretch',
})

export const StyledSubscriptionTitleContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: calcRem(8),
    alignSelf: 'stretch',
    height: calcRem(95),
})

export const StyledSubscriptionSubHeading = styled('p')({
    margin: 0,
    fontSize: calcRem(16),
    fontFamily: fonts.thewest.sansSerif,
    fontWeight: 400,
    lineHeight: calcRem(18),
    color: '#2B2D2E',
})

export const StyledSubscriptionHeading = styled('p')({
    margin: 0,
    fontSize: calcRem(28),
    fontFamily: fonts.thewest.serif,
    fontWeight: 700,
    lineHeight: calcRem(36),
    color: '#2B2D2E',
})

export const StyledSubscriptionPricingAndBenefitsContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: calcRem(16),
    flex: '1 0 0',
    alignSelf: 'stretch',
})

export const StyledSubscriptionPriceContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: calcRem(6),
    alignSelf: 'stretch',
})

export const StyledSubscriptionInnerPriceContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: calcRem(16),
    alignSelf: 'stretch',
})

export const StyledSubscriptionPriceTextContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: calcRem(8),
})

export const StyledSubscriptionPrice = styled('p')<{
    packageOptions: PackageOptions
    isDiscounted?: boolean
}>(({ packageOptions, isDiscounted, theme }) => [
    {
        margin: 0,
        fontSize: calcRem(32),
        fontFamily: fonts.thewest.sansSerifCond,
        fontWeight: 600,
        color: isDiscounted
            ? theme.colors.text.secondary
            : theme.colors.text.primary,
        lineHeight: calcRem(32),
        textTransform: 'uppercase',
        textDecoration: isDiscounted ? 'line-through' : 'none',
    },
    packageOptions.isRecommended === true && {
        color: isDiscounted ? theme.colors.text.secondary : theme.colors.brand,
        fontSize: calcRem(42),
        fontWeight: isDiscounted ? 600 : 700,
    },
])
export const StyledSubscriptionSubPriceText = styled('p')({
    margin: 0,

    color: '#2B2D2E',
    fontFamily: fonts.thewest.sansSerif,
    fontSize: calcRem(16),
    fontWeight: 400,
    lineHeight: calcRem(18),
})
export const StyledSubscriptionQualifierText = styled('p')({
    margin: 0,

    color: '#535556',
    fontFamily: fonts.thewest.sansSerif,
    fontSize: calcRem(16),
    fontWeight: 400,
    lineHeight: calcRem(18),
})

export const StyledRibbon = styled('div')<{ ribbonOption: RibbonOptions }>(
    ({ ribbonOption }) => [
        {
            display: 'flex',
            padding: calcRem(8, 16),
            alignItems: 'center',
            gap: calcRem(4),

            borderRadius: calcRem(72),
            background: 'rgba(4, 61, 226, 0.06)',

            span: {
                color: '#043DE2',
                margin: 0,
                fontFamily: fonts.thewest.sansSerif,
                fontSize: calcRem(16),
                fontWeight: 500,
                lineHeight: calcRem(18),
            },
        },
        ribbonOption === 'freeDeliveryBluePill' && {
            padding: calcRem(0, 16, 0, 0),
            gap: calcRem(8),
            background: '#043DE2',
            alignSelf: 'end',

            span: {
                color: '#FFF',
                fontWeight: 600,
            },
        },
    ],
)

export const StyledBenefitsContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: calcRem(8),
    alignSelf: 'stretch',
})

export const StyledBenefit = styled('img')({
    width: calcRem(20),
    height: calcRem(20),

    // backgroundImage: `url('${BENEFIT_TICK_ICON}')`,
})

export const StyledBenefitRowContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: calcRem(8),
    alignSelf: 'stretch',
})

export const StyledBenefitText = styled('p')<{ isBenefitAccessible: boolean }>(
    ({ isBenefitAccessible }) => [
        {
            color: '#2B2D2E',
            margin: 0,

            fontFamily: fonts.thewest.sansSerif,
            fontSize: calcRem(16),
            fontWeight: 400,
            lineHeight: calcRem(18),
        },
        isBenefitAccessible === false && {
            color: 'rgba(43, 45, 46, 0.30)',
        },
    ],
)

export const StyledRadioButtonContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: calcRem(8),
    alignSelf: 'stretch',
    padding: calcRem(12),

    borderRadius: calcRem(2),
    background: 'rgba(0, 0, 0, 0.03)',
})

export const StyledRadioRowContainer = styled('label')({
    display: 'flex',
    alignItems: 'center',
    gap: calcRem(8),
})

export const StyledRadioRowText = styled('p')({
    color: '#2B2D2E',
    margin: 0,

    fontFamily: fonts.thewest.sansSerif,
    fontSize: calcRem(16),
    fontWeight: 400,
    lineHeight: calcRem(20),
})

export const StyledSubscriptionButtonContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: calcRem(8),
    alignSelf: 'stretch',
})

export const StyledSubscriptionRadioInput = styled(SubscriptionRadioInput)({
    background: 'white',
})

export const StyledSubscribeButton = styled('button')<PackageThemeProps>(
    ({ buttonStyle }) => [
        // -- Base Styles (Applied to all packages)
        {
            fontSize: calcRem(18),
            lineHeight: calcRem(20),
            color: '#043DE2',
            padding: calcRem(12, 24),
            fontWeight: 500,
            width: '100%',
            border: 'none',
            borderRadius: calcRem(2),
            cursor: 'pointer',

            '&:hover, &:focus': {
                opacity: 0.8,
            },
        },
        // -- Button Style Variations
        buttonStyle === 'solid-blue' && {
            background: '#043DE2',
            color: '#FFF',
        },
        buttonStyle === 'light-blue' && {
            background: 'rgba(4, 61, 226, 0.06)',
            border: '1px solid #043DE2',
        },
    ],
)

export const StyledSwgButton = styled('button')(() => ({
    cursor: 'pointer',
    maxWidth: '100%',
    width: '100%',
    minHeight: calcRem(44),
    padding: calcRem(12, 64),
    borderRadius: calcRem(2),
    border: `1px solid ${colors.thewest.greySeal}`,
    boxShadow: `none`,

    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: calcRem(8),
    background: '#FFF',

    '&:hover, &:focus': {
        background: '#F5F5F5',
    },
    span: {
        color: '#043DE2',
        fontFamily: fonts.thewest.sansSerif,
        fontSize: calcRem(18),
        fontWeight: 500,
        lineHeight: calcRem(20),
    },
}))

export const StyledCancellationMessage = styled('p')(() => [
    {
        margin: 0,
        fontSize: calcRem(12),
        fontFamily: fonts.thewest.sansSerif,
        fontWeight: 400,
        lineHeight: calcRem(16),
        color: '#2B2D2E',
    },
])

export const StyledPackageImageContainer = styled('div')(() => [
    {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        maxHeight: calcRem(220),

        [breakpoint('sm')]: {
            maxHeight: calcRem(160),
        },
    },
])
export const StyledPackageImage = styled('img')(() => [
    {
        height: '100%',
    },
])
